import { ArtifactOpenButton } from "./ArtifactOpenButton";
import {
  useDocumentEditor,
  isEditableDocument,
} from "../document-editor/DocumentEditorContext";
import { t } from "../../i18n";
import { BookmarkPlus, Download, FileText, Pencil } from "lucide-react";
import { stringValue } from "../chat/ToolResultView.utils";
import { ArtifactMarkdownPreview } from "./ArtifactMarkdownPreview";
import {
  artifactDownloadUrl,
  isMarkdownArtifact,
  safeArtifactUrl,
} from "./artifact-utils";

export function ArtifactCard({
  artifact,
  featured = false,
  onSave,
  saving = false,
}: {
  artifact: Record<string, unknown>;
  featured?: boolean;
  onSave?: () => void;
  saving?: boolean;
}) {
  const editor = useDocumentEditor();
  const name = stringValue(artifact.name) || "Artifact";
  const mimeType = stringValue(artifact.mimeType);
  const url = safeArtifactUrl(stringValue(artifact.url));
  const downloadUrl = artifactDownloadUrl(
    stringValue(artifact.url),
    stringValue(artifact.downloadUrl),
  );
  const markdown = isMarkdownArtifact(name, mimeType);
  const sizeBytes =
    typeof artifact.sizeBytes === "number" ? artifact.sizeBytes : undefined;
  const imagePreview =
    mimeType.startsWith("image/") && mimeType !== "image/svg+xml" && url;
  return (
    <div
      className={`overflow-hidden rounded-xl border border-lumen-200 bg-lumen-50/60 dark:border-lumen-900/60 dark:bg-lumen-950/20 ${featured ? "p-0" : "p-3"}`}
    >
      {featured && imagePreview && (
        <img
          src={url}
          alt={name}
          className="max-h-72 w-full bg-stone-950/5 object-contain dark:bg-black/20"
        />
      )}
      <div className={`flex items-start gap-3 ${featured ? "p-4" : ""}`}>
        {!featured && imagePreview ? (
          <img
            src={url}
            alt={name}
            className="size-16 rounded-lg object-cover"
          />
        ) : (
          <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-white text-lumen-700 shadow-sm dark:bg-white/10 dark:text-lumen-300">
            <FileText size={16} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-stone-800 dark:text-stone-100">
            {name}
          </div>
          <div className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">
            {mimeType || "Artifact"}
            {sizeBytes !== undefined
              ? ` · ${Math.ceil(sizeBytes / 1024)} KB`
              : ""}
            {typeof artifact.version === "number" && artifact.version > 1
              ? ` · v${artifact.version}`
              : ""}
          </div>
          {stringValue(artifact.reference) && (
            <code className="mt-1 block truncate text-[11px] text-lumen-700 dark:text-lumen-300">
              {stringValue(artifact.reference)}
            </code>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {editor?.enabled && isEditableDocument(name) && (
            <button
              type="button"
              className="button min-h-10 gap-1 px-2 text-xs"
              onClick={() => editor.open(stringValue(artifact.id))}
              aria-label={`Edit ${name}`}
            >
              <Pencil size={14} /> Edit
            </button>
          )}
          {onSave && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="button gap-1 px-2 py-1 text-xs"
              title={t("Save to vault")}
            >
              <BookmarkPlus size={14} />
              {saving ? "Saving…" : "Save to vault"}
            </button>
          )}
          <ArtifactOpenButton
            id={stringValue(artifact.id)}
            name={name}
            url={url}
          />
          <a
            href={downloadUrl || undefined}
            className="rounded-lg p-1.5 text-stone-400 hover:bg-white hover:text-lumen-700 dark:hover:bg-white/10 dark:hover:text-lumen-300"
            title={t("Download artifact")}
          >
            <Download size={14} />
          </a>
        </div>
      </div>
      {markdown && (
        <ArtifactMarkdownPreview
          url={url}
          name={name}
          downloadUrl={downloadUrl}
          initialContent={stringValue(artifact.previewContent)}
          sizeBytes={sizeBytes}
        />
      )}
    </div>
  );
}
